import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "fs_launchpad_token";
const RETURN_KEY = "fs_launchpad_return_to";

// ---- token storage ----
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// ---- return-to path ----
export const setReturnTo = (path) => {
  if (path && path !== "/signin" && !path.startsWith("/auth/verify")) {
    sessionStorage.setItem(RETURN_KEY, path);
  }
};
export const takeReturnTo = () => {
  const v = sessionStorage.getItem(RETURN_KEY);
  if (v) sessionStorage.removeItem(RETURN_KEY);
  return v;
};

// ---- JWT parsing (client-side expiry hint only; server is authoritative) ----
export function decodeJwtExp(token) {
  try {
    const [, payload] = token.split(".");
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json.exp === "number" ? json.exp : null;
  } catch {
    return null;
  }
}

export function isTokenExpired(token) {
  const exp = decodeJwtExp(token);
  if (!exp) return false;
  return Date.now() / 1000 >= exp;
}

// ---- session-invalidation event bus ----
// AuthContext listens to this and triggers a single central re-auth screen.
const SESSION_INVALID_EVENT = "fs:session-invalid";
export const emitSessionInvalid = () => {
  window.dispatchEvent(new CustomEvent(SESSION_INVALID_EVENT));
};
export const onSessionInvalid = (handler) => {
  window.addEventListener(SESSION_INVALID_EVENT, handler);
  return () => window.removeEventListener(SESSION_INVALID_EVENT, handler);
};

// ---- axios client with request/response interceptors ----
export const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) {
    // Proactive local check to avoid firing calls with an obviously-dead token.
    if (isTokenExpired(t)) {
      clearToken();
      emitSessionInvalid();
    } else {
      cfg.headers.Authorization = `Bearer ${t}`;
    }
  }
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = (err?.config?.url || "").toString();
    // Auth endpoints handle 401/400 themselves; do not blow away the session.
    const isAuthCall = url.includes("/auth/magic/") || url.endsWith("/auth/me");
    if (status === 401 && !isAuthCall) {
      clearToken();
      emitSessionInvalid();
    }
    return Promise.reject(err);
  }
);
